import Vue from 'vue'
import CDNClient from '@frontend/cdn-uploader'
import { sizeFilter } from './utils'
import axios from 'axios'

/**
 * cmao文件上传实例
 * 可直接用sdk提供的方法
 */
const CDNClientOption = {
  env: window.CONFIG.env === 'development' ? 'dev' : window.CONFIG.env,
  projectName: 'blueboat',
  bucketCode: window.CONFIG.bucketCodePublic
}
export const cdnUploadClient = new CDNClient(CDNClientOption)

export const cdnUploadClientPrivate = new CDNClient({
  ...CDNClientOption,
  bucketCode: window.CONFIG.bucketCodePrivate
})

const MAX_SIZE = 200 * 1024 * 1024 // CDN最大为200MB

/**
 * @description cmao 文件上传二次封装，主要做文件校验用
 * 服务端上传报错，不一定会触发onerror，所以在前端先验证类型、文件大小等，不传则不验证
 * @param {!File} file 文件，必传
 * @param {?Object}  config 配置项
 * @param {?String}  config.type 'normal'（正常上传）|'transcode'（转低码率视频）|'slice'（转成m3u8格式视频）默认是：normal
 * @param {?Array}   config.persistentOption 视频切片中的视频转码参数
 * @param {?Boolean} config.insertOnly 新增模式，true只能新增，不能覆盖和修改. 可选. 默认true
 * @param {?Boolean} config.closeTips 是否需要提示，默认有，可通过业务控制
 * @param {?String}  config.filename 文件名，也可以前面加路径
 * @param {?String}  config.accept 标准 MIME_type 类型格式，如 audio/*,video/*,image/* 中间用,分割
 * @param {?Number}  config.limit 文件大小，最大值受CND仓库限制，通过 cmdb/审批管理/静态资源配置变更 走工单审批
 * @param {?Boolean} config.limitText 提示文字 '图片最大为'
 * @param {?Function}config.onprogress 上传进度
 * @returns { Promise } {url, filename, hash}
 */

export const sdkUploader = (file, config) => {
  try {
    if (!file) {
      console.error('file is required')
      return Promise.reject('请选择文件')
    }

    const options = config || {}
    // 文件类型验证
    if (options.accept) {
      // 去除空格，用,分割
      // eslint-disable-next-line no-useless-escape
      const types = options.accept.replace(/\ /g, '').split(',')
      // 支持的格式，除*，如 [png, mp4, ogg]
      const typesString = types.map(item => item.split('/')[1]).filter(item => item !== '*')
      const isAllowed = types.some(type => {
        return new RegExp(type).test(file.type)
      })
      if (!isAllowed) {
        !options.closeTips &&
          Vue.prototype.$message.error(
            typesString.length ? `仅支持 ${typesString.join('、')} 格式` : '文件格式错误'
          )
        return Promise.reject({
          type: 'mime',
          message: typesString.length ? `仅支持 ${typesString.join('、')} 格式` : '文件格式错误'
        })
      }
    }

    // 文件大小验证
    if (file.size > MAX_SIZE || (options.limit && file.size > options.limit)) {
      const max = sizeFilter(options.limit ? options.limit : MAX_SIZE, 0)
      const tipStr = options.limitText ? options.limitText : `文件最大为${max}`
      !options.closeTips && Vue.prototype.$message.error(tipStr)
      return Promise.reject({
        type: 'size',
        message: tipStr
      })
    }

    return new Promise(async (resolve, reject) => {
      const client = options.isPrivate ? cdnUploadClientPrivate : cdnUploadClient
      const uploader = await client.create(file, {
        filename: options.filename ? options.filename : Date.now().toString() + file.name,
        onprogress: options.onprogress || function() {},
        onsuccess: res => {
          console.log('success', res)
          resolve(res)
        },
        onerror: e => {
          reject(e)
        }
      })
      if (!uploader) {
        console.log('初始化sdk失败')
        return
      }
      uploader.start()
    })
  } catch (e) {
    return Promise.reject({
      type: 'js',
      message: e
    })
  }
}

/**
 * 获取七牛资源token、播放地址
 * 保持同步 blueboat 项目
 * @param {!String}
 * @returns {
 *  bucket_url: "https://dev-static-edu.codemao.cn/"
 *  download_url: "https://dev-static-edu.codemao.cn/dev/17/sdk_upload/1625643468000/name.mp4?e=1625645324&token=Z7VJBYDfapvJhzJLXXsy-M4OqxaUPlrRP6pslxKr:MvghnS8OhszKSgwMeIhYpfTrcXw="
 *  file_path: "dev/17/sdk_upload/1625643468000/name.mp4"
 *  token: ""
 * }
 */
export const sdkDownloading = async (url, config = {}) => {
  const res = await axios.get(`${window.CONFIG.open_service}/cdn/qi-niu/tokens/downloading`, {
    params: {
      cdnName: 'qiniu',
      filePath: url,
      projectName: config.projectName || 'redboat' // redboat默认使用共有仓库，但是视频从 blueboat 上传是加密的（需要token签名），获取的话也需要从对应业务获取
    },
    withCredentials: true
  })
  if (res.status !== 200) {
    Vue.prototype.$message.error('获取下载链接失败')
    return Promise.reject('error')
  }
  return res.data
}

/**
 * 获取资源地址（对sdkDownloading方法做容错处理）
 *
 * @export
 * @param {string} path - 路径地址
 * @returns
 */
export async function getResourceUrl(path, config = {}) {
  if (!path) {
    return ''
  }
  const data = await sdkDownloading(path, config)
  if (data) {
    return data.download_url
  }
  return ''
}
